import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { modifyUser } from "../../../store/thunks/userThunk";
import BankingInstitutionNumberTable from "../../../utils/BankingInstitutionNumberTable";
import { ALL_ROLES } from "../../../constants/roles";

const UserHrInformation = ({ user = {}, onComplete }) => {
  const dispatch = useDispatch();
  const [saveStatus, setSaveStatus] = useState("initial");
  const [editMode, setEditMode] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const userUser = user.user;

  console.log("recived user (actually.. response) in hrinformation", user);
  console.log("received user.user in userhrinformation", userUser);
  console.log("received userid from adduser", user.userId);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      employmentStatus: user.employmentStatus || "",
      activeStatus: user.userStatus || "",
      payrollType: user.payrollType || "",
      employeeId: user.employeeId || "",
      hireDate: user.startDate
        ? new Date(user.startDate).toISOString().split("T")[0]
        : "",
      terminationDate: user.terminationDate
        ? new Date(user.terminationDate).toISOString().split("T")[0]
        : "",
      jobTitle: user.jobTitle || "",
      department: user.department || "",
      role: Array.isArray(user.role)
        ? user.role.filter(Boolean)
        : [user.role].filter(Boolean),
      bankName: user.bankName || "",
      institutionNumber: user.institutionNumber || "",
      accountNumber: user.accountNumber || "",
      branchNumber: user.branchNumber || "",
      socialInsuranceNumber: user.socialInsuranceNumber || "",
      defaultPayPercentage: user.defaultPayPercentage || 45,
      qualifications: user.qualifications || "",
      invoiceDescriptor: user.invoiceDescriptor || "",
      calendarColor: user.calendarColor || "",
      calendarDisplayName: user.calendarDisplayName || "",
      specialization: Array.isArray(user.specialization)
        ? user.specialization.filter(Boolean)
        : [user.specialization].filter(Boolean),
    },
    validationSchema: Yup.object({
      employmentStatus: Yup.string().required("Required"),
      activeStatus: Yup.string().required("Required"),
      hireDate: Yup.date().required("Required"),
      terminationDate: Yup.date().nullable(),
      jobTitle: Yup.string().required("Required"),
      department: Yup.string().required("Required"),
      role: Yup.array().min(1, "At least one role must be selected"),
      bankName: Yup.string(),
      institutionNumber: Yup.string(),
      accountNumber: Yup.string(),
      branchNumber: Yup.string(),
      socialInsuranceNumber: Yup.string(),
      defaultPayPercentage: Yup.string(),
    }),
    onSubmit: async (values) => {
      setSaveStatus("saving");

      const getChangedFields = (values, original) => {
        const changedFields = {};

        for (const key in values) {
          if (
            JSON.stringify(values[key]) !== JSON.stringify(original[key]) &&
            values[key] !== ""
          ) {
            changedFields[key] = values[key];
          }
        }

        return changedFields;
      };

      const userData = getChangedFields(values, {
        ...user,
        startDate: user.startDate,
        userStatus: user.userStatus,
      });

      // Rename specific fields to match the backend schema
      if (userData.activeStatus) userData.userStatus = userData.activeStatus;
      if (userData.hireDate) userData.startDate = userData.hireDate;

      // Filter out any null or undefined values from specialization
      if (userData.specialization) {
        userData.specialization = userData.specialization.filter(Boolean);
      }

      // Add the `__t` field if `role` includes 'clinician'
      if (userData.role && userData.role.includes("clinician")) {
        userData.__t = "clinician";
      }

      // Remove temporary fields
      delete userData.activeStatus;
      delete userData.hireDate;

      console.log("Modifying user with data:", userData);

      try {
        const response = await dispatch(modifyUser({ userId: user._id || user.userId, userData })).unwrap();
        console.log("User modified successfully:", response);
        setSaveStatus("saved");
        setEditMode(false);
        onComplete(response);

        // Show success modal
        setShowSuccessModal(true);

        // Reset the button text after 2 seconds
        setTimeout(() => {
          setSaveStatus("initial");
          setShowSuccessModal(false);
        }, 2000);
      } catch (error) {
        console.error("Error modifying user:", error);
        // Check if the error is due to duplicate employee ID
        if (error?.message?.includes('duplicate key error') && error?.message?.includes('employeeId')) {
          setSaveStatus('duplicate-id');
        } else {
          setSaveStatus("error");
        }
      }
    },
  });

  const specializations = ["SLP", "CDA"];

  const handleBankChange = (event) => {
    const selectedBank = event.target.value;
    const bankingTable = BankingInstitutionNumberTable();
    const selectedInstitution =
      bankingTable.props.children.props.children[1].props.children.find(
        (bank) => bank.props.children[0].props.children === selectedBank
      );
    formik.setFieldValue("bankName", selectedBank);
    formik.setFieldValue(
      "institutionNumber",
      selectedInstitution
        ? selectedInstitution.props.children[1].props.children
        : ""
    );
  };

  return (
    <div className="space-y-6">
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-sm shadow-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">Success</h3>
                <p className="text-sm text-gray-600 mt-1">
                  HR information updated successfully.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header with Edit Toggle */}
      <div className="flex justify-between items-center pb-4 border-b border-gray-100">
        <div>
          <h2 className="text-lg font-medium text-gray-900">HR Information</h2>
          <p className="text-sm text-gray-500 mt-1">Employment details and financial information</p>
        </div>
        <button
          type="button"
          onClick={() => setEditMode(!editMode)}
          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors"
        >
          {editMode ? 'Cancel' : 'Edit'}
        </button>
      </div>

      <form onSubmit={formik.handleSubmit} className="space-y-6">
        {/* Employment Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900">Employment Information</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="employmentStatus" className="block text-sm font-medium text-gray-700 mb-1">
                Employment Status *
              </label>
              <select
                id="employmentStatus"
                name="employmentStatus"
                disabled={!editMode}
                {...formik.getFieldProps("employmentStatus")}
                className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              >
                <option value="">Select status</option>
                <option value="Full-time">Full-time</option>
                <option value="Part-time">Part-time</option>
                <option value="Contract">Contract</option>
                <option value="Temporary">Temporary</option>
              </select>
              {formik.touched.employmentStatus && formik.errors.employmentStatus && (
                <p className="text-red-600 text-xs mt-1">{formik.errors.employmentStatus}</p>
              )}
            </div>

            <div>
              <label htmlFor="activeStatus" className="block text-sm font-medium text-gray-700 mb-1">
                Active Status *
              </label>
              <select
                id="activeStatus"
                name="activeStatus"
                disabled={!editMode}
                {...formik.getFieldProps("activeStatus")}
                className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive (Cannot login)</option>
              </select>
              {formik.touched.activeStatus && formik.errors.activeStatus && (
                <p className="text-red-600 text-xs mt-1">{formik.errors.activeStatus}</p>
              )}
            </div>

            <div>
              <label htmlFor="employeeId" className="block text-sm font-medium text-gray-700 mb-1">
                Employee ID
              </label>
              <input
                type="text"
                name="employeeId"
                disabled={!editMode}
                {...formik.getFieldProps("employeeId")}
                className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              />
              {formik.touched.employeeId && formik.errors.employeeId && (
                <p className="text-red-600 text-xs mt-1">{formik.errors.employeeId}</p>
              )}  
            </div>

            <div>
              <label htmlFor="payrollType" className="block text-sm font-medium text-gray-700 mb-1">
                Payroll Type
              </label>
              <select
                id="payrollType"
                name="payrollType"
                disabled={!editMode}
                {...formik.getFieldProps("payrollType")}
                className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              >
                <option value="fee for service">Fee for Service</option>
                <option value="hourly">Hourly</option>
              </select>
              {formik.touched.payrollType && formik.errors.payrollType && (
                <p className="text-red-600 text-xs mt-1">{formik.errors.payrollType}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="hireDate" className="block text-sm font-medium text-gray-700 mb-1">
                Hire Date *
              </label>
              <input
                type="date"
                name="hireDate"
                id="hireDate"
                disabled={!editMode}
                {...formik.getFieldProps("hireDate")}
                className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              />
              {formik.touched.hireDate && formik.errors.hireDate && (
                <p className="text-red-600 text-xs mt-1">{formik.errors.hireDate}</p>
              )}
            </div>

            <div>
              <label htmlFor="terminationDate" className="block text-sm font-medium text-gray-700 mb-1">
                Termination Date
              </label>
              <input
                type="date"
                name="terminationDate"
                id="terminationDate"
                disabled={!editMode}
                {...formik.getFieldProps("terminationDate")}
                className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              />
              {formik.touched.terminationDate && formik.errors.terminationDate && (
                <p className="text-red-600 text-xs mt-1">{formik.errors.terminationDate}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700 mb-1">
                Job Title *
              </label>
              <input
                type="text"
                name="jobTitle"
                id="jobTitle"
                disabled={!editMode}
                {...formik.getFieldProps("jobTitle")}
                className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              />
              {formik.touched.jobTitle && formik.errors.jobTitle && (
                <p className="text-red-600 text-xs mt-1">{formik.errors.jobTitle}</p>
              )}
            </div>

            <div>
              <label htmlFor="department" className="block text-sm font-medium text-gray-700 mb-1">
                Department *
              </label>
              <select
                id="department"
                name="department"
                disabled={!editMode}
                {...formik.getFieldProps("department")}
                className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              >
                <option value="">Select department</option>
                <option value="Speech">Speech</option>
                <option value="Audiology">Audiology</option>
                <option value="Corporate">Corporate</option>
              </select>
              {formik.touched.department && formik.errors.department && (
                <p className="text-red-600 text-xs mt-1">{formik.errors.department}</p>
              )}
            </div>
          </div>
        </div>

        {/* Role and Specialization */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900">Role and Specialization</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Role *</label>
              <div className="space-y-2">
                {ALL_ROLES.map((role) => (
                  <div key={role} className="flex items-center">
                    <input
                      id={`role-${role}`}
                      name="role"
                      type="checkbox"
                      value={role}
                      disabled={!editMode}
                      checked={formik.values.role.includes(role)}
                      onChange={formik.handleChange}
                      className="h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 rounded disabled:opacity-50"
                    />
                    <label htmlFor={`role-${role}`} className="ml-3 text-sm text-gray-700">
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </label>
                  </div>
                ))}
              </div>
              {formik.touched.role && formik.errors.role && (
                <p className="text-red-600 text-xs mt-1">{formik.errors.role}</p>
              )}
            </div>

            {formik.values.role.includes("clinician") && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Specialization</label>
                <div className="space-y-2">
                  {specializations.map((spec) => (
                    <div key={spec} className="flex items-center">
                      <input
                        id={`specialization-${spec}`}
                        name="specialization"
                        type="checkbox"
                        value={spec}
                        disabled={!editMode}
                        checked={formik.values.specialization.includes(spec)}
                        onChange={formik.handleChange}
                        className="h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 rounded disabled:opacity-50"
                      />
                      <label htmlFor={`specialization-${spec}`} className="ml-3 text-sm text-gray-700">
                        {spec}
                      </label>
                    </div>
                  ))}
                </div>
                {formik.touched.specialization && formik.errors.specialization && (
                  <p className="text-red-600 text-xs mt-1">{formik.errors.specialization}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Additional Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900">Additional Information</h3>
          
          <div>
            <label htmlFor="qualifications" className="block text-sm font-medium text-gray-700 mb-1">
              Qualifications
            </label>
            <textarea
              id="qualifications"
              name="qualifications"
              rows="3"
              disabled={!editMode}
              {...formik.getFieldProps("qualifications")}
              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
            />
            {formik.touched.qualifications && formik.errors.qualifications && (
              <p className="text-red-600 text-xs mt-1">{formik.errors.qualifications}</p>
            )}
          </div>

          <div>
            <label htmlFor="invoiceDescriptor" className="block text-sm font-medium text-gray-700 mb-1">
              Invoice Descriptor
            </label>
            <input
              type="text"
              id="invoiceDescriptor"
              name="invoiceDescriptor"
              disabled={!editMode}
              {...formik.getFieldProps("invoiceDescriptor")}
              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
            />
            {formik.touched.invoiceDescriptor && formik.errors.invoiceDescriptor && (
              <p className="text-red-600 text-xs mt-1">{formik.errors.invoiceDescriptor}</p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="calendarColor" className="block text-sm font-medium text-gray-700 mb-1">
                Calendar Color
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  id="calendarColor"
                  name="calendarColor"
                  disabled={!editMode}
                  {...formik.getFieldProps("calendarColor")}
                  className="h-10 w-16 p-1 rounded-sm border border-gray-300 disabled:opacity-50"
                />
                <input
                  type="text"
                  value={formik.values.calendarColor}
                  disabled={!editMode}
                  onChange={(e) => {
                    let value = e.target.value;
                    if (!value.startsWith("#")) value = "#" + value;
                    formik.setFieldValue("calendarColor", value);
                  }}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                  placeholder="#000000"
                />
              </div>
              {formik.touched.calendarColor && formik.errors.calendarColor && (
                <p className="text-red-600 text-xs mt-1">{formik.errors.calendarColor}</p>
              )}
            </div>

            <div>
              <label htmlFor="calendarDisplayName" className="block text-sm font-medium text-gray-700 mb-1">
                Calendar Display Name
              </label>
              <input
                type="text"
                id="calendarDisplayName"
                name="calendarDisplayName"
                disabled={!editMode}
                {...formik.getFieldProps("calendarDisplayName")}
                className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              />
              {formik.touched.calendarDisplayName && formik.errors.calendarDisplayName && (
                <p className="text-red-600 text-xs mt-1">{formik.errors.calendarDisplayName}</p>
              )}
            </div>
          </div>
        </div>

        {/* Financial Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900">Financial Information</h3>
          
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="bankName" className="block text-sm font-medium text-gray-700 mb-1">
                Bank Name
              </label>
              <select
                name="bankName"
                id="bankName"
                disabled={!editMode}
                onChange={handleBankChange}
                onBlur={formik.handleBlur}
                value={formik.values.bankName}
                className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              >
                <option value="">Select a bank</option>
                {BankingInstitutionNumberTable().props.children.props.children[1].props.children.map(
                  (bank) => (
                    <option
                      key={bank.key}
                      value={bank.props.children[0].props.children}>
                      {bank.props.children[0].props.children}
                    </option>
                  )
                )}
              </select>
              {formik.touched.bankName && formik.errors.bankName && (
                <p className="text-red-600 text-xs mt-1">{formik.errors.bankName}</p>
              )}
            </div>

            <div>
              <label htmlFor="institutionNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Institution Number
              </label>
              <input
                type="text"
                name="institutionNumber"
                id="institutionNumber"
                {...formik.getFieldProps("institutionNumber")}
                readOnly
                className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm bg-gray-50 text-gray-500"
              />
              {formik.touched.institutionNumber && formik.errors.institutionNumber && (
                <p className="text-red-600 text-xs mt-1">{formik.errors.institutionNumber}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Account Number
              </label>
              <input
                type="text"
                name="accountNumber"
                id="accountNumber"
                disabled={!editMode}
                {...formik.getFieldProps("accountNumber")}
                className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              />
              {formik.touched.accountNumber && formik.errors.accountNumber && (
                <p className="text-red-600 text-xs mt-1">{formik.errors.accountNumber}</p>
              )}
            </div>

            <div>
              <label htmlFor="branchNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Branch Number
              </label>
              <input
                type="text"
                name="branchNumber"
                id="branchNumber"
                disabled={!editMode}
                {...formik.getFieldProps("branchNumber")}
                className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              />
              {formik.touched.branchNumber && formik.errors.branchNumber && (
                <p className="text-red-600 text-xs mt-1">{formik.errors.branchNumber}</p>
              )}
            </div>

            <div>
              <label htmlFor="socialInsuranceNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Social Insurance Number
              </label>
              <input
                type="text"
                name="socialInsuranceNumber"
                id="socialInsuranceNumber"
                disabled={!editMode}
                {...formik.getFieldProps("socialInsuranceNumber")}
                className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              />
              {formik.touched.socialInsuranceNumber && formik.errors.socialInsuranceNumber && (
                <p className="text-red-600 text-xs mt-1">{formik.errors.socialInsuranceNumber}</p>
              )}
            </div>
          </div>

          {formik.values.payrollType === "fee for service" && (
            <div>
              <label htmlFor="defaultPayPercentage" className="block text-sm font-medium text-gray-700 mb-1">
                Default Pay Percentage
              </label>
              <input
                type="text"
                name="defaultPayPercentage"
                id="defaultPayPercentage"
                disabled={!editMode}
                {...formik.getFieldProps("defaultPayPercentage")}
                className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 sm:max-w-xs"
              />
              {formik.touched.defaultPayPercentage && formik.errors.defaultPayPercentage && (
                <p className="text-red-600 text-xs mt-1">{formik.errors.defaultPayPercentage}</p>
              )}
            </div>
          )}
        </div>

        {/* Save Button */}
        {editMode && (
          <div className="flex justify-end pt-4 border-t border-gray-100">
            <div className="flex flex-col items-end space-y-2">
              {saveStatus === 'duplicate-id' && (
                <p className="text-sm text-red-600">This Employee ID already exists. Please choose a different one.</p>
              )}
              <button
                type="submit"
                disabled={saveStatus === "saving"}
                className={`inline-flex justify-center px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                  saveStatus === "saving"
                    ? "bg-gray-400 text-white cursor-not-allowed"
                    : saveStatus === "saved"
                    ? "bg-green-600 text-white"
                    : saveStatus === "error" || saveStatus === "duplicate-id"
                    ? "bg-red-600 text-white"
                    : "bg-gray-900 text-white hover:bg-gray-800"
                }`}
              >
                {saveStatus === "saving"
                  ? "Saving..."
                  : saveStatus === "saved"
                  ? "Saved!"
                  : saveStatus === "duplicate-id"
                  ? "Error: Duplicate ID"
                  : saveStatus === "error"
                  ? "Error"
                  : "Save Changes"}
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default UserHrInformation;
